import { createAsyncThunk } from '@reduxjs/toolkit';
import { mapOrganizationSummary } from '../../pages/internalOps/iam/api/iamMappers';
import { iamBaseUrl, iamRequest, toIamRejectPayload } from '../utils/iamRequest';

const baseUrl = iamBaseUrl;

export const fetchIamOrganizationSummary = createAsyncThunk(
    'iam/fetchOrganizationSummary',
    async (organizationUlid, thunkAPI) => {
        try {
            const { payload, serviceMessage } = await iamRequest({
                method: 'post',
                url: `${baseUrl()}/organization`,
                data: { organization_ulid: organizationUlid },
            });
            return {
                serviceMessage,
                summary: mapOrganizationSummary(payload, organizationUlid),
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(toIamRejectPayload(error));
        }
    },
);

export const fetchTradeKnotConfig = createAsyncThunk('iam/fetchTradeKnotConfig', async (organizationUlid, thunkAPI) => {
    try {
        return await iamRequest({
            method: 'get',
            url: `${baseUrl()}/iam/${organizationUlid}/trade-knots`,
        });
    } catch (error) {
        return thunkAPI.rejectWithValue(toIamRejectPayload(error));
    }
});

export const saveTradeKnotConfig = createAsyncThunk(
    'iam/saveTradeKnotConfig',
    async ({ organizationUlid, tradeKnotsEnabled }, thunkAPI) => {
        try {
            return await iamRequest({
                method: 'put',
                url: `${baseUrl()}/iam/${organizationUlid}/trade-knots`,
                data: { enabled: !!tradeKnotsEnabled },
            });
        } catch (error) {
            return thunkAPI.rejectWithValue(toIamRejectPayload(error));
        }
    },
);

export const fetchDealKnotConfig = createAsyncThunk('iam/fetchDealKnotConfig', async (organizationUlid, thunkAPI) => {
    try {
        return await iamRequest({
            method: 'get',
            url: `${baseUrl()}/iam/${organizationUlid}/deal-knots`,
        });
    } catch (error) {
        return thunkAPI.rejectWithValue(toIamRejectPayload(error));
    }
});

export const saveDealKnotConfig = createAsyncThunk(
    'iam/saveDealKnotConfig',
    async ({ organizationUlid, dealKnotsEnabled }, thunkAPI) => {
        try {
            return await iamRequest({
                method: 'put',
                url: `${baseUrl()}/iam/${organizationUlid}/deal-knots`,
                data: { enabled: !!dealKnotsEnabled },
            });
        } catch (error) {
            return thunkAPI.rejectWithValue(toIamRejectPayload(error));
        }
    },
);

/** Open Knots main switch — GET /iam/{organizationUlid}/open-knots */
export const fetchOpenKnotsSwitch = createAsyncThunk('iam/fetchOpenKnotsSwitch', async (organizationUlid, thunkAPI) => {
    try {
        return await iamRequest({
            method: 'get',
            url: `${baseUrl()}/iam/${organizationUlid}/open-knots`,
        });
    } catch (error) {
        return thunkAPI.rejectWithValue(toIamRejectPayload(error));
    }
});

/** Open Knots main switch — PATCH /iam/{organizationUlid}/open-knots */
export const saveOpenKnotsSwitch = createAsyncThunk(
    'iam/saveOpenKnotsSwitch',
    async ({ organizationUlid, openKnotsEnabled }, thunkAPI) => {
        try {
            return await iamRequest({
                method: 'put',
                url: `${baseUrl()}/iam/${organizationUlid}/open-knots`,
                data: { enabled: !!openKnotsEnabled },
            });
        } catch (error) {
            return thunkAPI.rejectWithValue(toIamRejectPayload(error));
        }
    },
);

export const fetchIamWhatsapp = createAsyncThunk('iam/fetchWhatsapp', async (organizationUlid, thunkAPI) => {
    try {
        return await iamRequest({
            method: 'get',
            url: `${baseUrl()}/iam/${organizationUlid}/whatsapp`,
        });
    } catch (error) {
        return thunkAPI.rejectWithValue(toIamRejectPayload(error));
    }
});

export const saveIamWhatsapp = createAsyncThunk(
    'iam/saveWhatsapp',
    async ({ organizationUlid, whatsappPhoneNumberId }, thunkAPI) => {
        try {
            return await iamRequest({
                method: 'put',
                url: `${baseUrl()}/iam/${organizationUlid}/whatsapp`,
                data: { whatsapp_phone_number_id: String(whatsappPhoneNumberId ?? '').trim() },
            });
        } catch (error) {
            return thunkAPI.rejectWithValue(toIamRejectPayload(error));
        }
    },
);

export const fetchLinkedIndividuals = createAsyncThunk(
    'iam/fetchLinkedIndividuals',
    async (organizationUlid, thunkAPI) => {
        try {
            return await iamRequest({
                method: 'get',
                url: `${baseUrl()}/iam/${organizationUlid}/linked-individuals`,
            });
        } catch (error) {
            return thunkAPI.rejectWithValue(toIamRejectPayload(error));
        }
    },
);

export const saveLinkedIndividualsNotifications = createAsyncThunk(
    'iam/saveLinkedIndividualsNotifications',
    async ({ organizationUlid, linkedIndividuals }, thunkAPI) => {
        try {
            return await iamRequest({
                method: 'patch',
                url: `${baseUrl()}/iam/linked-individuals/notifications`,
                data: {
                    organization_ulid: organizationUlid,
                    linked_individuals: linkedIndividuals,
                },
            });
        } catch (error) {
            return thunkAPI.rejectWithValue(toIamRejectPayload(error));
        }
    },
);

export const fetchTokenSummary = createAsyncThunk('iam/fetchTokenSummary', async (organizationUlid, thunkAPI) => {
    try {
        return await iamRequest({
            method: 'get',
            url: `${baseUrl()}/iam/${organizationUlid}/tokens`,
        });
    } catch (error) {
        return thunkAPI.rejectWithValue(toIamRejectPayload(error));
    }
});

export const allocateTokens = createAsyncThunk('iam/allocateTokens', async ({ organizationUlid, amount }, thunkAPI) => {
    try {
        return await iamRequest({
            method: 'post',
            url: `${baseUrl()}/iam/tokens/allocate`,
            data: { organization_ulid: organizationUlid, amount },
        });
    } catch (error) {
        return thunkAPI.rejectWithValue(toIamRejectPayload(error));
    }
});

/** GET /iam/{organizationUlid}/open-knots/linked-whatsapp-numbers */
export const fetchLinkedWhatsappNumbers = createAsyncThunk(
    'iam/fetchLinkedWhatsappNumbers',
    async (organizationUlid, thunkAPI) => {
        try {
            return await iamRequest({
                method: 'get',
                url: `${baseUrl()}/iam/${organizationUlid}/open-knots/linked-whatsapp-numbers`,
            });
        } catch (error) {
            return thunkAPI.rejectWithValue(toIamRejectPayload(error));
        }
    },
);

/** POST /iam/{organizationUlid}/open-knots/linked-whatsapp-numbers */
export const createLinkedWhatsappNumber = createAsyncThunk(
    'iam/createLinkedWhatsappNumber',
    async ({ organizationUlid, contactName, whatsappNumber }, thunkAPI) => {
        try {
            return await iamRequest({
                method: 'post',
                url: `${baseUrl()}/iam/${organizationUlid}/open-knots/linked-whatsapp-numbers`,
                data: {
                    contact_name: String(contactName ?? '').trim(),
                    whatsapp_number: String(whatsappNumber ?? '').trim(),
                },
            });
        } catch (error) {
            return thunkAPI.rejectWithValue(toIamRejectPayload(error));
        }
    },
);

/** PUT /iam/{organizationUlid}/open-knots/linked-whatsapp-numbers/{mappingUlid} */
export const updateLinkedWhatsappNumber = createAsyncThunk(
    'iam/updateLinkedWhatsappNumber',
    async ({ organizationUlid, mappingUlid, contactName, whatsappNumber }, thunkAPI) => {
        try {
            return await iamRequest({
                method: 'put',
                url: `${baseUrl()}/iam/${organizationUlid}/open-knots/linked-whatsapp-numbers/${mappingUlid}`,
                data: {
                    contact_name: String(contactName ?? '').trim(),
                    whatsapp_number: String(whatsappNumber ?? '').trim(),
                },
            });
        } catch (error) {
            return thunkAPI.rejectWithValue(toIamRejectPayload(error));
        }
    },
);

/** DELETE /iam/{organizationUlid}/open-knots/linked-whatsapp-numbers/{mappingUlid} */
export const deleteLinkedWhatsappNumber = createAsyncThunk(
    'iam/deleteLinkedWhatsappNumber',
    async ({ organizationUlid, mappingUlid }, thunkAPI) => {
        try {
            return await iamRequest({
                method: 'delete',
                url: `${baseUrl()}/iam/${organizationUlid}/open-knots/linked-whatsapp-numbers/${mappingUlid}`,
            });
        } catch (error) {
            return thunkAPI.rejectWithValue(toIamRejectPayload(error));
        }
    },
);
